import { Grid, withStyles, WithStyles, Theme } from '@material-ui/core'
import { goBack } from 'connected-react-router'
import i18n from 'i18next'
import { FC, Fragment } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { formValueSelector } from 'redux-form'
import { openDialog, closeDialog } from '../../../actions/dialogs'
import { navigate } from '../../../actions/routes'
import {
  addShowingToFieldArray,
  createShowings
} from '../../../actions/showings'
import { OpenDialog, CloseDialog, Navigate } from '../../../actions/types'
import GridContainer from '../../../components/Grid/GridContainer'
import { selectSelectedApplications } from '../../../selectors/applications'
import {
  selectContentIdFromPathname,
  selectUserIdFromPathname
} from '../../../selectors/router'
import { selectShowingsLoading } from '../../../selectors/showings'
import { selectIsMobile } from '../../../selectors/users'
import { MuiClasses } from '../../../types'
import { AddShowingsFormData, FormShowing } from '../../../types/showings'
import { StateInterface, CreateShowingsFormState } from '../../../types/states'
import { bindSubmitActionToPromise } from '../../../utils/forms'
import Dialog from '../../Dialog'
import { AddShowingsForm, CreateShowingsForm } from './Forms'

type StateProps = {
  selectedApplications: string[]
  showingsLoading: boolean
  isMobile: boolean | undefined
  showings?: FormShowing
  apartmentId: string | undefined
  userId: string | undefined
}

type DispatchProps = {
  openDialog: OpenDialog
  closeDialog: CloseDialog
  addShowingToFieldArray: (formData: AddShowingsFormData) => void
  createShowingsAction: (formData: CreateShowingsFormState) => void
  navigateToPath: Navigate
  goBack: () => void
}
interface CreateShowingProps extends WithTranslation {
  classes: MuiClasses
  t: i18n.TFunction
}
type ComponentProps = CreateShowingProps &
  StateProps &
  DispatchProps &
  WithStyles

const styles = ({ breakpoints, spacing }: Theme) => ({
  container: {
    maxWidth: '1280px',
    padding: '13px 10px',
    margin: 'auto',
    fontFamily: 'CircularRegular',
    display: 'flex',
    flexDirection: 'column' as const,
    '@media (max-width: 414px)': {
      width: '99%'
    }
  },

  heading: {
    paddingTop: '20px',
    margin: '1rem 0rem 1rem 0rem',
    [breakpoints.down('sm')]: {
      fontSize: '1.2rem',
      paddingTop: '0'
    }
  },
  helperText: {
    display: 'flex',
    paddingLeft: '5px'
  },
  addShowingsFormPaper: {
    display: 'flex'
  },
  buttonContainer: {
    marginTop: '35px'
  },
  buttonNext: {
    borderRadius: '10px',
    padding: '14px',
    fontWeight: 400,
    lineHeight: 'normal',
    fontSize: '13px'
  },
  confirmButton: {
    alignSelf: 'flex-end',
    paddingBottom: '35px',
    marginTop: '10px',
    [breakpoints.only('xs')]: {
      alignSelf: 'flex-start'
    }
  },
  dialogContent: {
    fontFamily: 'CircularRegular'
  },
  paddingTop: {
    paddingTop: '5px'
  },
  createWarningContainer: {
    margin: '35px 0'
  },
  createWarning: {
    whiteSpace: 'pre-line' as const
  },
  createShowingsForm: {
    [breakpoints.up('md')]: {
      width: '70%'
    },
    [breakpoints.down('sm')]: { width: '100%' }
  },
  fabDivLeft: {
    alignSelf: 'flex-start',
    [breakpoints.up('sm')]: {
      padding: spacing(4)
    },
    [breakpoints.only('xs')]: {
      padding: spacing(1),
      paddingLeft: spacing(2)
    }
  },
  fabDivLeftTitle: {
    color: '#C3C6D1',
    fontSize: '14px',
    marginLeft: '8px'
  },
  showingsList: {
    width: '100%',
    display: 'flex',
    'flex-direction': 'column',
    margin: '2rem 0 1rem 0'
  }
})

const CreateShowings: FC<ComponentProps> = ({
  classes,
  addShowingToFieldArray,
  createShowingsAction,
  isMobile,
  openDialog,
  t,
  closeDialog,
  selectedApplications,
  showingsLoading,
  navigateToPath,
  userId,
  apartmentId,
  goBack,
  showings
}) => {
  const gridContainerProps = {
    className: classes.container,
    isMobile
  }

  const onFormSubmit = () => {
    openDialog(`ShowingsConfirmationDialog`)
  }

  return (
    <Fragment>
      <GridContainer {...gridContainerProps}>
        <Grid
          container
          direction='row'
          justify='space-evenly'
          className={classes.addShowingsFormContainer}
        >
          <Grid item xs={12}>
            <Grid container direction='row' justify='space-evenly'>
              <Grid sm={12}>
                <AddShowingsForm
                  addShowingToFieldArray={addShowingToFieldArray}
                  onFormSubmit={onFormSubmit}
                  userId={userId}
                  apartmentId={apartmentId}
                  classes={classes}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Dialog
          dialogId={'ShowingsConfirmationDialog'}
          title={t('showings.createShowings.confirmShowingsTitle')}
          noOpenDialogButton
          Content={
            <div className={classes.dialogContent}>
              <h5>Olet kutsumassa seuraavat henkilöt:</h5>
              <ul>
                {selectedApplications.map((application) => (
                  <li key={application}>{application}</li>
                ))}
              </ul>
              <CreateShowingsForm
                {...{
                  isConfirmDialog: true,
                  closeDialog,
                  createShowingsAction
                }}
              />
            </div>
          }
        />
      </GridContainer>
    </Fragment>
  )
}

const selector = formValueSelector('CreateShowingsForm')

const mapStateToProps = (state: StateInterface) => {
  return {
    showingsLoading: selectShowingsLoading(state),
    isMobile: selectIsMobile(state),
    selectedApplications: selectSelectedApplications(state),
    showings: selector(state, 'showings'),
    apartmentId: selectContentIdFromPathname(state),
    userId: selectUserIdFromPathname(state)
  }
}
const mapDispatchToProps = (dispatch: Dispatch) => ({
  openDialog: (id: string) => dispatch(openDialog(id)),
  closeDialog: (id: string) => dispatch(closeDialog(id)),
  createShowingsAction: bindSubmitActionToPromise(dispatch, createShowings),
  addShowingToFieldArray: bindSubmitActionToPromise(
    dispatch,
    addShowingToFieldArray
  ),
  navigateToPath: (path: string) => dispatch(navigate(path)),
  goBack: () => dispatch(goBack())
})

export default connect<
  StateProps,
  DispatchProps,
  CreateShowingProps,
  StateInterface
>(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(withStyles(styles)(CreateShowings)))
